import React, { useEffect, useState } from 'react';
import { getWritingTaskOptionsForExam } from '@examflow/exams';
import { LoadingSpinner } from '@examflow/ui';
import { api, type TrainingAttemptRecord, type TrainingAttemptQuestionMetric } from '../../lib/api';
import { resolveWritingTopicLabels } from './openEndedWriting';

type WritingHistoryPanelProps = {
  examType: string;
  skillId: string;
  onClose: () => void;
};

function formatTime(completedAt: number): string {
  try {
    return new Date(completedAt).toLocaleString('zh-CN', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(completedAt);
  }
}

function formatDuration(seconds: number): string {
  if (seconds <= 0) return '';
  if (seconds < 60) return `${seconds} 秒`;
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return rem > 0 ? `${minutes} 分 ${rem} 秒` : `${minutes} 分钟`;
}

function pickWritingMetric(
  record: TrainingAttemptRecord
): TrainingAttemptQuestionMetric | undefined {
  return (record.questionMetrics ?? []).find(
    (item) => item.responseMode === 'open_ended' || typeof item.aiScore === 'number'
  );
}

function resolveTaskLabel(
  examType: string,
  taskNumber: number | null | undefined
): string | null {
  if (taskNumber == null || taskNumber <= 0) return null;
  const option = getWritingTaskOptionsForExam(examType).find(
    (item) => item.value === taskNumber
  );
  return option?.label ?? `任务 ${taskNumber}`;
}

export function WritingHistoryPanel({ examType, skillId, onClose }: WritingHistoryPanelProps) {
  const [records, setRecords] = useState<TrainingAttemptRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void api.learning
      .getTrainingRecords({
        examType,
        skillId,
        moduleId: skillId,
        limit: 20,
      })
      .then((response) => {
        if (cancelled) return;
        const writingRecords = (response.records ?? []).filter((record) =>
          (record.questionMetrics ?? []).some(
            (metric) => metric.responseMode === 'open_ended' || typeof metric.aiScore === 'number'
          )
        );
        setRecords(writingRecords);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : '加载历史评分失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [examType, skillId]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="历史评分"
        className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">历史评分</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm text-slate-600 hover:bg-slate-100"
          >
            关闭
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex justify-center py-10">
              <LoadingSpinner />
            </div>
          )}
          {!loading && error && <p className="text-sm text-danger-600">{error}</p>}
          {!loading && !error && records.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">还没有写作评分记录。</p>
          )}
          {!loading && !error && records.length > 0 && (
            <ul className="space-y-3">
              {records.map((record) => {
                const metric = pickWritingMetric(record);
                const taskLabel = resolveTaskLabel(examType, metric?.taskNumber);
                const topicLabels = resolveWritingTopicLabels(examType, metric?.topics, skillId);
                const metaParts = [
                  taskLabel,
                  ...topicLabels.slice(0, 2),
                  metric?.level ? metric.level : null,
                  metric?.wordCount != null ? `${metric.wordCount} 词` : null,
                  formatDuration(record.durationSeconds) || null,
                ].filter(Boolean);

                return (
                  <li
                    key={record.id}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800">
                        {formatTime(record.completedAt)}
                      </span>
                      {metric?.aiScore != null && (
                        <span className="text-sm font-semibold text-primary-700">
                          {metric.aiGradeSource === 'heuristic' ? '参考分 ' : ''}
                          {metric.aiScore}/{metric.aiScoreMax ?? 20}
                          {metric.aiLevelHint ? ` · ${metric.aiLevelHint}` : ''}
                        </span>
                      )}
                    </div>
                    {metaParts.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {metaParts.map((part) => (
                          <span
                            key={`${record.id}-${part}`}
                            className="rounded-md bg-white px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200/80"
                          >
                            {part}
                          </span>
                        ))}
                      </div>
                    )}
                    {metric?.aiFeedback && (
                      <p className="mt-2 line-clamp-3 text-sm text-slate-700">{metric.aiFeedback}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
